import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HTTP_ENDPOINTS } from "../../../consts/api";
import { http } from "../../../services/HttpService";
import { Input } from "../../../shared/Input/Input.js";
import { InputLabel } from "../../../shared/InputLabel/InputLabel.js";
import { Button } from "../../../shared/Button/Button.js";
import { CircularProgress } from "../../../shared/CircularProgress/CircularProgress.js";
import { uiNotification } from "../../../services/UINotificationService.js";
import styles from "../JobSimulator/scss/JobSimulator.module.scss";

const STORAGE_KEY = "map-clearent-run-id";

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const Summary = ({ summary }) => {
  if (!summary) {
    return null;
  }

  return (
    <ul>
      <li>processed: {summary.processed}</li>
      <li>patients found: {summary.found}</li>
      <li>patients missing: {summary.missing}</li>
      <li>cards: {summary.cards}</li>
      <li>failed: {summary.failed}</li>
    </ul>
  );
};

const MapClearentToStripe = () => {
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState(null);
  const [runId, setRunId] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || "",
  );

  useEffect(() => {
    if (runId) {
      sessionStorage.setItem(STORAGE_KEY, runId);
    }
  }, [runId]);

  const statusQuery = useQuery(
    ["mapClearentImport", runId],
    () => http.get(HTTP_ENDPOINTS.mapClearentImportStatus(runId)),
    {
      enabled: Boolean(runId),
      select: (response) => response.data,
      refetchInterval: (data) => {
        const payload = data?.dry_run ? data : data?.data;
        const dryRunRunning = payload?.dry_run?.status === "running";
        const finalRunning = payload?.final?.status === "running";
        return dryRunRunning || finalRunning ? 3000 : false;
      },
    },
  );

  const startDryRun = useMutation(
    () => {
      const formData = new FormData();
      formData.append("account_id", accountId);
      formData.append("file", file);
      return http.post(HTTP_ENDPOINTS.mapClearentImport(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    {
      onSuccess: (response) => {
        setRunId(response.data.id);
        uiNotification.success("Dry run started.");
      },
      onError: (error) =>
        uiNotification.error(
          errorMessage(error, "Could not start the dry run."),
        ),
    },
  );

  const startLiveRun = useMutation(
    () => http.post(HTTP_ENDPOINTS.mapClearentImportRun(runId)),
    {
      onSuccess: () => uiNotification.success("Live import started."),
      onError: (error) =>
        uiNotification.error(
          errorMessage(error, "Could not start the live import."),
        ),
    },
  );

  const run = statusQuery.data;
  const dryRun = run?.dry_run;
  const finalRun = run?.final;
  const canRunLive = dryRun?.status === "done" && finalRun?.status === "idle";

  return (
    <div className="container" style={{ padding: 24, maxWidth: 720 }}>
      <h1>Map Clearent to Stripe</h1>
      <div className={styles.simulatorContainer}>
        <p>
          Upload the Stripe migration file and the account id. The dry run does
          not change patients or call Stripe. After you check the log, start the
          live import. Steps are in{" "}
          <a
            href="https://app.clickup.com/31512304/docs/y1nqg-29887/y1nqg-73813"
            target="_blank"
            rel="noopener noreferrer"
          >
            Map Clearent tokens to Stripe
          </a>
          .
        </p>
        <p>
          The file can be the usual CSV, or the JSON result Stripe sends now.
          The separate error CSV is the list of cards Stripe rejected. Those
          cards are not mapped.
        </p>
        <div>
          <InputLabel>
            Account id<span className="setting-require">*</span>
          </InputLabel>
          <Input
            className={styles.paramInput}
            inputClassName={styles.input}
            type="text"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          />
        </div>
        <div>
          <InputLabel>
            Migration file<span className="setting-require">*</span>
          </InputLabel>
          <input
            type="file"
            accept=".csv,.json,text/csv,application/json"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
          />
        </div>
        <Button
          onClick={() => startDryRun.mutate()}
          isDisabled={!accountId || !file || startDryRun.isLoading}
          rightAdornment={
            startDryRun.isLoading ? (
              <CircularProgress size="small" color="white" />
            ) : null
          }
        >
          Run dry-run
        </Button>

        {statusQuery.isError && (
          <p>
            {errorMessage(statusQuery.error, "Could not load this import.")}
          </p>
        )}

        {run && (
          <div>
            <p>
              Account {run.account_id}
              {run.account_name ? ` — ${run.account_name}` : ""} (
              {run.account_status})
            </p>
            <p>
              {run.original_name}: {run.rows} cards, {run.unique_customers}{" "}
              customers
            </p>
            <p>Dry run: {dryRun.status}</p>
            <Summary summary={dryRun.summary} />
            {dryRun.log_url && (
              <p>
                <a
                  href={dryRun.log_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open dry-run log
                </a>
              </p>
            )}
            <Button
              color="warn"
              onClick={() => {
                const confirmed = window.confirm(
                  `Run the live import for account ${run.account_id} (${run.account_name})? This updates patients and calls Stripe.`,
                );
                if (confirmed) {
                  startLiveRun.mutate();
                }
              }}
              isDisabled={!canRunLive || startLiveRun.isLoading}
              rightAdornment={
                startLiveRun.isLoading || finalRun.status === "running" ? (
                  <CircularProgress size="small" color="white" />
                ) : null
              }
            >
              Run live import
            </Button>
            {finalRun.status !== "idle" && (
              <>
                <p>Live import: {finalRun.status}</p>
                <Summary summary={finalRun.summary} />
                {finalRun.log_url && (
                  <p>
                    <a
                      href={finalRun.log_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open live import log
                    </a>
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapClearentToStripe;
